import loadable from '@loadable/component'
import React, { MouseEvent, useRef } from 'react'
import { useQuery } from 'urql'

import DealCard from '../deal-card'
import { getContent } from '../deal-card/components/more-info-slide-out/content'
import Hero from '../hero/hero'
import LandingForm from '../landing-form/form-types/standard'
import Tabs from '../tabs'
import { usePageContext } from '~/contexts/page-context'
import { FragmentType } from '~/gql'
import { Deal } from '~/gql/graphql'
import AdFeedFragment from '~/graphql/fragments/ad-fields'
import SINGLE_DEAL_QUERY from '~/graphql/queries/single-deal'
import { RESULTS_PATH } from '~/routes-list'
import { clickAnchor, getSegmentProps, trackDealClick } from '~/utils/tracking'

import styles from './more-info.styles.module.css'

const MetaData = loadable(() => import('../meta-data/meta-data'))

const MoreInfo = () => {
  const {
    path,
    pageId,
    listnames,
    helmet: { metaContent }
  } = usePageContext()
  const dealLinkRef = useRef(null)

  const PATH_ID_INDEX = -2

  const dealId = path.split('/').slice(PATH_ID_INDEX)[0]

  const [{ error, data }] = useQuery({
    query: SINGLE_DEAL_QUERY,
    variables: { productIds: dealId },
    requestPolicy: 'cache-first',
    pause: false
  })

  if (error) {
    return (
      <div className={styles.wrapper}>
        <MetaData
          metaTitle={`More Info 404`}
          metaDescription={''}
          metaContent={{
            noindex: true
          }}
          webpageSchema={undefined}
          preConnectUrl={undefined}
        />

        <h1>More Info 404</h1>
      </div>
    )
  }

  const deal = data?.dealsEmail?.deals[0] as Deal

  const list = listnames().table

  const position = 2

  const placementType = `p${position}`

  const adType = ''

  const comparisonId = ''

  const tenancy = {
    position: {
      exposure: 'p',
      position
    },
    display: {
      productId: dealId,
      title: deal.title,
      imageUrl: '',
      usp: [],
      kevelClickUrl: '',
      kevelImpressionUrl: '',
      deal
    }
  } as FragmentType<typeof AdFeedFragment>

  const clickoutLinkUrl = deal?.clickoutLinks?.[0]?.url

  const segmentProps = getSegmentProps({
    pageId,
    deal,
    comparisonId,
    position,
    dealUrl: clickoutLinkUrl,
    adType,
    placementType
  })

  const onClickDealCard = (e: MouseEvent<Element>) => {
    e.stopPropagation()

    if ((e.target as HTMLElement).tagName.toLowerCase() !== 'a') return clickAnchor(e, dealLinkRef)

    trackDealClick({
      segmentProps,
      adType,
      ecommerce: '',
      kevelClickUrl: clickoutLinkUrl,
      tenancy: '',
      eventLabelSuffix: ' more info page'
    })
  }

  return (
    <div className={styles.wrapper}>
      <MetaData
        metaTitle={`${deal.title} More Info`}
        metaDescription={''}
        metaContent={{ ...metaContent, canonical: `https://www.uswitch.com${path}` }}
        webpageSchema={undefined}
        preConnectUrl={undefined}
      />

      <Hero p0Fetched={{ current: false }}>
        <div className={styles.hero}>
          <h1 className={styles.title}>{deal.title}</h1>

          <ul className={styles.dealCardWrapper}>
            <DealCard
              deal={deal}
              tenancy={tenancy}
              list={list}
              position={position}
              adType={adType}
              placementType={placementType}
              href={clickoutLinkUrl}
              onClick={onClickDealCard}
              isTvHomepage={false}
              hasUspAnimated={true}
              setHasUspAnimated={() => true}
              isSponsored={false}
              segmentProps={segmentProps}
            />
          </ul>
        </div>
      </Hero>

      <div className={styles.container}>
        <div className={`${styles.column} ${styles.moreInfoContent}`}>
          <Tabs tabs={getContent(deal)} sku={segmentProps.product.sku} />
        </div>

        <div className={`${styles.column} ${styles.addressInput}`}>
          <span className={`${styles.addressInputText} ${styles.heading}`}>Check out best deals in your area</span>

          <LandingForm
            redirect={RESULTS_PATH}
            customerAddress={[]}
            onUpdate={() => {}}
            showProviderBox={true}
            provider=''
            buttonText={'Find deals'}
            isFetchingAddress={false}
            providersOverride={null}
          />
        </div>
      </div>
    </div>
  )
}

export default MoreInfo
